import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import Navbar from '../../Utils/Navbar/navbar-component';
import { getLandingData } from '../../Public/landing-data';
import SystemDataService from '../../Services/SystemDataService';

function Seo({ title, description, keywords, image }) {
  const location = useLocation();

  // 1. Get system data including businessInfo
  const systemData = useSelector((s) => s.systemData || {});
  const { language = 'he', businessInfo = {} } = systemData;
  const { website } = businessInfo;
  const twitterLink = SystemDataService.byLabel(systemData.links, 'Twitter');

  // 2. Get Defaults from LandingData
  const landingData = getLandingData(language);
  const defaults = landingData.seo;

  // 3. Fallbacks
  const finalTitle = title || defaults.title;
  const finalDescription = (description || defaults.description).slice(0, 160);
  const finalKeywords = keywords || defaults.keywords;
  const finalImage = image || businessInfo.mainLogo;

  // 4. Construct Canonical URL using businessInfo.website
  // We ensure there's no double slash if the website string ends with /
  const cleanBaseUrl = website?.replace(/\/$/, '') || '';
  const canonicalUrl = `${cleanBaseUrl}${location.pathname}`;

  return (
    <Helmet>
      {/* --- Basic Metadata --- */}
      <title>{finalTitle}</title>
      <link rel="canonical" href={canonicalUrl} />

      <meta name="description" key="description" content={finalDescription} />
      <meta name="keywords" key="keywords" content={finalKeywords} />

      {/* --- Open Graph (Facebook, LinkedIn, etc.) --- */}
      <meta property="og:url" key="og:url" content={canonicalUrl} />
      <meta property="og:type" key="og:type" content="website" />
      <meta property="og:title" key="og:title" content={finalTitle} />
      <meta property="og:description" key="og:description" content={finalDescription} />
      <meta property="og:image" key="og:image" content={finalImage} />

      {/* --- X (Twitter) Cards --- */}
      <meta name="twitter:card" key="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" key="twitter:title" content={finalTitle} />
      <meta name="twitter:description" key="twitter:description" content={finalDescription} />
      <meta name="twitter:image" key="twitter:image" content={finalImage} />

      {/* Optional:  X handle if you have one, e.g., content="@FlowmaAgency" */}
      {twitterLink?.description && <meta name="twitter:site" key="twitter:site" content={twitterLink?.description} />}

      {/* --- Robots --- */}
      <meta name="robots" content="index, follow" />
    </Helmet>
  );
}

function Layout({ children, seo = {} }) {
  return (
    <>
      <Seo
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        image={seo.image}
      />
      <Navbar />

      <main className="pt-24 lg:pt-28 min-h-screen">
        {children}
      </main>
    </>
  );
}

export default Layout;